import { useNavigate } from "react-router-dom";
import { useAuth } from "../../core/auth";
import { gql, useMutation } from "@apollo/client";
import BottomTab from "../../components/common/bottom-tab";
import Header from "../../components/common/header";
import { useState } from "react";
import Modal from "../../components/common/modal";
import useAmplitudeContext from "../../core/use-amplitude";
import { notifyError } from "../../utils/toast";
const WEB_URL = window.location.origin;

interface DeleteUserResult {
  ok: boolean;
  error?: string | null;
}

interface KakaoDeleteUserMutation {
  kakaoDeleteUser: DeleteUserResult;
}

interface AppleDeleteUserMutation {
  appleDeleteUser: DeleteUserResult;
}

interface AppleDeleteUserMutationVariables {
  code: string;
}

interface PureDeleteUserMutation {
  pureDeleteUser: DeleteUserResult;
}

const DEFAULT_DELETE_ERROR_MESSAGE =
  "탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해주세요.";

const KAKAO_DELETE_USER = gql`
  mutation KakaoDeleteUser {
    kakaoDeleteUser {
      ok
      error
    }
  }
`;
const APPLE_DELETE_USER = gql`
  mutation AppleDeleteUser($code: String!) {
    appleDeleteUser(code: $code) {
      ok
      error
    }
  }
`;
const PURE_DELETE_USER = gql`
  mutation PureDeleteUser {
    pureDeleteUser {
      ok
      error
    }
  }
`;
const SignOut = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [kakaoDeleteUser] =
    useMutation<KakaoDeleteUserMutation>(KAKAO_DELETE_USER);
  const [appleDeleteUser] = useMutation<
    AppleDeleteUserMutation,
    AppleDeleteUserMutationVariables
  >(APPLE_DELETE_USER);
  const [pureDeleteUser] =
    useMutation<PureDeleteUserMutation>(PURE_DELETE_USER);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearSignOutStorage = () => {
    localStorage.removeItem("jwt");
    localStorage.removeItem("isKakao");
    localStorage.removeItem("isApple");
  };

  const completeDelete = () => {
    clearSignOutStorage();
    logout();
    navigate("/");
  };

  const handleDeleteResult = (result: DeleteUserResult | undefined) => {
    if (!result?.ok) {
      notifyError(result?.error ?? DEFAULT_DELETE_ERROR_MESSAGE);
      return;
    }

    completeDelete();
  };

  async function onClickDelete() {
    if (isDeleting) return;

    const isKakao = localStorage.getItem("isKakao");
    const isApple = localStorage.getItem("isApple");
    setIsDeleting(true);

    try {
      if (isKakao) {
        const { data } = await kakaoDeleteUser();
        handleDeleteResult(data?.kakaoDeleteUser);
        return;
      }

      if (isApple) {
        window.AppleID.auth.init({
          clientId: "com.luke7299.mahi-sign-in",
          scope: "email",
          redirectURI: `${WEB_URL}/apple-auth`,
          state: "hey",
          usePopup: true,
        });

        const res = await window.AppleID.auth.signIn();
        const code = res?.authorization.code;

        if (!code) {
          notifyError("Apple 인증 코드가 없어 탈퇴를 진행할 수 없습니다.");
          return;
        }

        const { data } = await appleDeleteUser({
          variables: {
            code,
          },
        });
        handleDeleteResult(data?.appleDeleteUser);
        return;
      }

      const { data } = await pureDeleteUser();
      handleDeleteResult(data?.pureDeleteUser);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : DEFAULT_DELETE_ERROR_MESSAGE;
      notifyError(message);
    } finally {
      setIsDeleting(false);
    }
  }
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("이유를 선택해주세요");
  const options = [
    "메뉴가 한정적이에요.",
    "매장 수가 너무 적어요.",
    "우리 동네 근처에 매장이 없어요.",
    "앱에 버그가 너무 많아요.",
    "기타",
  ];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  const handleOptionClick = (option: string) => {
    setSelectedOption(option); // Update the selected option
    setIsOpen(false); // Close the dropdown
  };

  const [isOpenModal, setIsOpenModal] = useState(false);

  const toggleModal = () => {
    setIsOpenModal(!isOpenModal);
  };
  const { trackAmplitudeEvent } = useAmplitudeContext();

  return (
    <div className="w-full h-[100vh]  flex flex-col justify-between">
      <div>
        <Header title="탈퇴하기" showBackButton />

        <div className="mt-12 px-4">
          <div className="mb-[10px] text-black text-3xl font-semibold font-['Pretendard'] leading-[31.20px]">
            고객님과의 이별인가요?
            <br />
            너무 아쉬워요.
          </div>
          <div className="mb-[10px]  h-14 text-black text-md font-normal font-['Pretendard'] leading-[21px]">
            지금까지 저희 마감히어로를 사랑해주셔서 감사합니다.
            <br />
            부족한 점이 있었다면 누구보다 빠르게 개선하도록 하겠습니다.
          </div>
          <div className="mb-[10px] text-black text-md font-semibold font-['Pretendard'] leading-[21px]">
            계정을 삭제하려는 이유가 궁금해요.
          </div>
          <div
            onClick={toggleDropdown}
            className="relative flex items-center justify-between w-full max-w-[335px] h-[54px] rounded-lg border border-[#757575] p-4"
          >
            <div className="text-[#c4c4c4] text-sm font-normal font-['Pretendard'] leading-[21px]">
              {selectedOption}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M14.25 6.75L9 12L3.75 6.75"
                stroke="#757575"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {isOpen && (
            <div className="mt-2 bg-white border border-[#757575] rounded-lg shadow-lg">
              {options.map((option, index) => (
                <div
                  key={index}
                  onClick={() => handleOptionClick(option)} // Set selected option and close dropdown
                  className="px-4 py-2 text-sm text-[#757575] hover:bg-gray-100 cursor-pointer"
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Submit Button */}
      <div className="px-4 flex justify-center w-full mb-[80px]">
        <button
          onClick={toggleModal}
          className="w-full max-w-[430px] h-[54px] bg-[#757575] text-white rounded-lg border border-[#757575] hover:bg-[#636363] transition-colors"
        >
          제출하기
        </button>
      </div>
      <BottomTab />
      <Modal
        isOpen={isOpenModal}
        title="정말 탈퇴하시겠습니까?"
        onYes={() => {
          trackAmplitudeEvent("계정탈퇴", { reason: selectedOption });
          onClickDelete();
        }}
        onClose={toggleModal}
        yesButtonDisabled={isDeleting}
      />
    </div>
  );
};

export default SignOut;
